import React, { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Compass } from "lucide-react";
import { api } from "../lib/config";
import { Reveal, SplitLines } from "../components/Motion";
import TripCard from "../components/TripCard";
import PrivateTripBanner from "../components/PrivateTripBanner";
import Seo from "../components/Seo";

const HERO_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_explore-once/artifacts/kynnso1w_IMG_1404.JPG.webp";

const MANIFESTO = [
  { n: "01", t: "Small groups", c: "Fourteen travellers max. Real conversations at the dinner table." },
  { n: "02", t: "Real travelers on the ground", c: "Tejas and Aishwarya lead almost every trip themselves." },
  { n: "03", t: "One-of-one moments", c: "Sunrise on a private beach. A chef we've known for a decade." },
  { n: "04", t: "Slow, then fast", c: "Days you'll never forget, punctuated by mornings you'll never forget either." },
];

export default function Home() {
  const [trips, setTrips] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  useEffect(() => {
    api.get("/trips", { params: { featured: true, trip_type: "Group" } }).then((r) => setTrips(r.data));
    api.get("/blogs").then((r) => setBlogs(r.data.slice(0, 3)));
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="Some Trips Only Happen Once"
        description="Once Way Travel — boutique small-group journeys curated by Aishwarya & Tejas. Curated once-in-a-lifetime trips across Kenya, Kyrgyzstan and beyond."
        path="/"
      />
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden grain">
        <motion.div style={{ scale: heroScale, y: heroY }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Rice terraces" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-b from-owt-ink/40 via-transparent to-owt-ink/60"/>
        </motion.div>

        <div className="absolute top-24 md:top-28 left-5 md:left-10 z-10 font-mono text-[10px] tracking-[0.3em] uppercase text-owt-surface/80">
        </div>

        <div className="relative z-10 h-full flex flex-col justify-end px-5 md:px-10 pb-16 md:pb-24 max-w-[1600px] mx-auto">
          <h1 className="font-display text-owt-surface tracking-tighter leading-[0.9] text-[16vw] md:text-[10vw] lg:text-[9rem] font-bold">
            <SplitLines text="Some Trips" delay={0.3}/>
            <span className="block italic text-owt-terracotta">
              <SplitLines text="Only Happen Once." delay={0.6}/>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.9 }}
            className="mt-8 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          >
            <Link
              to="/trips"
              data-testid="hero-cta"
              className="inline-flex items-center gap-3 bg-owt-surface text-owt-forest px-6 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-owt-terracotta hover:text-owt-surface transition-colors"
            >
              Join the Group Trip Now <ArrowUpRight className="w-4 h-4"/>
            </Link>
            <div className="text-owt-surface/80 max-w-sm text-sm">
              Curated small-group journeys, hosted by a travel couple who've been to 17 countries.
            </div>
          </motion.div>
        </div>

        {/* compass at bottom-left */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7 }}
          className="hidden md:block absolute bottom-8 right-10 text-owt-surface/70 z-10"
        >
          <Compass className="w-16 h-16 compass-spin" strokeWidth={0.6} />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="py-14 border-y border-owt-border bg-owt-bg overflow-hidden">
        <Marquee gradient={false} speed={40}>
          {["Bali", "Iceland", "Kenya", "Japan", "Kyrgyzstan", "Patagonia", "Vietnam", "Morocco", "Norway", "Bhutan"].map((w, i) => (
            <span key={i} className="font-display text-6xl md:text-[8rem] marquee-outline mx-8 leading-none">
              {w} <span className="text-owt-terracotta">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <Reveal>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-6">The Manifesto</div>
              <h2 className="font-display text-5xl md:text-6xl leading-[0.95] text-owt-forest">
                We chase the trips
                <br/>
                <span className="italic text-owt-terracotta">worth the passport stamp.</span>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:col-start-6 grid sm:grid-cols-2 gap-x-8 gap-y-12">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.08}>
                <div className="font-mono text-owt-terracotta text-3xl mb-3">{m.n}</div>
                <h3 className="font-display text-2xl text-owt-forest mb-2">{m.t}</h3>
                <p className="text-owt-muted leading-relaxed">{m.c}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED TRIPS */}
      <section className="py-24 md:py-32 border-t border-owt-border">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="flex items-end justify-between mb-12 md:mb-16 flex-wrap gap-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-3">Featured Group Trips</div>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.95] text-owt-forest max-w-3xl">
                Currently boarding.
              </h2>
            </div>
            <Link to="/trips" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-owt-forest hover:text-owt-terracotta" data-testid="view-all-trips">
              View All Trips <ArrowUpRight className="w-4 h-4"/>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {trips.slice(0, 3).map((t, i) => <TripCard key={t.id} trip={t} index={i}/>)}
          </div>
        </div>
      </section>

      {/* PRIVATE TRIP BANNER */}
      <PrivateTripBanner />

      {/* BLOG PREVIEW */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-3">Journal</div>
              <h2 className="font-display text-5xl md:text-6xl text-owt-forest leading-[0.95]">Field notes.</h2>
            </div>
            <Link to="/blog" className="font-mono text-[11px] tracking-[0.25em] uppercase inline-flex items-center gap-2 text-owt-forest hover:text-owt-terracotta">Read all →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {blogs.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.08}>
                <Link to={`/blog/${b.slug}`} className="group block" data-testid={`home-blog-${b.slug}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-owt-sand">
                    <motion.img src={b.cover_image} alt={b.title} className="w-full h-full object-cover" whileHover={{ scale: 1.06 }} transition={{ duration: 1 }}/>
                  </div>
                  <div className="mt-4">
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-terracotta">{b.category.replace(/-/g, " ")} · {b.read_time} min</div>
                    <h3 className="font-display text-2xl text-owt-forest mt-2 group-hover:text-owt-terracotta transition-colors">{b.title}</h3>
                    <p className="text-owt-muted mt-2">{b.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
